/**
 * Central staff-auth consumer (client half). Contract per
 * optisigns-staff-app/docs/staff-auth-consumer-recipe.md:
 *   GSI button → Google idToken → POST <staff-api>/auth/token → { accessToken,
 *   refreshToken, expiresIn } stored in localStorage; silent refresh at
 *   expiresIn-180s; 401 → clear + re-login.
 *
 * Local-dev bypass: VITE_STAFF_AUTH_DISABLED=true skips the gate entirely so
 * the dashboard works against an api running with STAFF_AUTH_DISABLED=true.
 * localStorage is the accepted internal-app tradeoff (recipe D12).
 */
import { useCallback, useEffect, useState } from 'react';
import { disablePush } from './push';

const ACCESS_KEY = 'infras.access';
const REFRESH_KEY = 'infras.refresh';

const AUTH_URL = import.meta.env.VITE_STAFF_AUTH_URL || 'http://localhost:4001';

export function authDisabled(): boolean {
  return import.meta.env.VITE_STAFF_AUTH_DISABLED === 'true';
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}
function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user?: { email: string; roles: string[] };
}

async function exchangeGoogle(googleIdToken: string): Promise<TokenPair> {
  const res = await fetch(`${AUTH_URL}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ googleIdToken }),
  });
  if (!res.ok) throw new Error(`token exchange failed (${res.status})`);
  return res.json();
}

async function refresh(): Promise<TokenPair | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  const res = await fetch(`${AUTH_URL}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) {
    clearTokens();
    return null;
  }
  const pair: TokenPair = await res.json();
  setTokens(pair.accessToken, pair.refreshToken);
  return pair;
}

/** Minimal auth state hook. authed is true immediately when auth is disabled. */
export function useAuth() {
  const [authed, setAuthed] = useState<boolean>(authDisabled() || !!getAccessToken());
  const [user, setUser] = useState<TokenPair['user']>();

  const scheduleRefresh = useCallback((expiresIn: number) => {
    const ms = Math.max((expiresIn - 180) * 1000, 30_000);
    window.setTimeout(async () => {
      const pair = await refresh();
      if (pair) scheduleRefresh(pair.expiresIn);
      else setAuthed(false);
    }, ms);
  }, []);

  const loginWithGoogle = useCallback(
    async (googleIdToken: string) => {
      const pair = await exchangeGoogle(googleIdToken);
      setTokens(pair.accessToken, pair.refreshToken);
      setUser(pair.user);
      setAuthed(true);
      scheduleRefresh(pair.expiresIn);
    },
    [scheduleRefresh],
  );

  const logout = useCallback(async () => {
    const refreshToken = getRefreshToken();
    if (refreshToken) {
      await fetch(`${AUTH_URL}/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      }).catch(() => undefined);
    }
    // Drop this browser's push subscription too — otherwise a shared machine keeps
    // receiving the signed-out person's notifications. Best-effort and time-boxed:
    // sign-out must never hang on it. The boot re-register is the backstop.
    await disablePush().catch(() => null);
    clearTokens();
    setAuthed(false);
  }, []);

  // On mount with a stored token, refresh once to validate + get a fresh window.
  useEffect(() => {
    if (authDisabled()) return;
    if (getAccessToken()) {
      refresh().then((pair) => {
        if (pair) scheduleRefresh(pair.expiresIn);
        else setAuthed(false);
      });
    }
  }, [scheduleRefresh]);

  return { authed, user, loginWithGoogle, logout };
}
